import { z } from 'zod';
import { EmailDeliveryStatus, Id, MessageAuthorType, MessageKind, Timestamp } from './common.js';
import { MESSAGE_MEDIA_MAX, MessageMediaOutput, PORTAL_MEDIA_LIMITS, PortalMessageMediaOutput } from './media.js';

/** Longueur maximale d'un message du garage. */
export const MESSAGE_BODY_MAX = 5000;

// ─── Vague 6 : événements du dossier dans la conversation ───────────────────

/**
 * Événements du dossier inscrits dans la conversation (message `kind: 'event'`, auteur `system`), **dans la
 * transaction de l'action**, sans texte libre stocké (type + référence) ; rendus par le serveur à la lecture.
 * Liste minimale : un événement par fait que le client doit voir ou sur lequel il doit agir, et qui a déjà
 * son e-mail (aucun e-mail ni push de plus) :
 * - `complement_sent` : version envoyée (première ou révision : même action, même e-mail) ;
 * - `change_requested` : demande de modification **saisie par le garage** (téléphone, sur place) ; sur le
 *   portail, c'est le commentaire du client, message `text` lié au complément, qui l'inscrit ;
 * - `photos_published` : photos publiées (nombre) ;
 * - `vehicle_ready` : véhicule prêt ;
 * - `report_available` : compte rendu de sortie disponible ;
 * - `entry_report_available` : fiche de réception partagée avec le client (ajout 10).
 * Exclus : l'accord, le refus et le report (la carte du complément les montre), la réponse du garage (déjà
 * un message), le rappel de gardiennage (rappel, pas un fait nouveau), l'entrée du véhicule (avant la conversation).
 */
export const DOSSIER_EVENT_TYPES = [
  'complement_sent',
  'change_requested',
  'photos_published',
  'vehicle_ready',
  'report_available',
  'entry_report_available',
] as const;
export const DossierEventType = z.enum(DOSSIER_EVENT_TYPES);
export type DossierEventType = z.infer<typeof DossierEventType>;

/**
 * Événement rendu par le serveur (`renderConversation` du cœur) : phrase et bouton, **jamais de montant**
 * (le montant reste sur la carte du complément, filtré par `amounts.view` côté garage).
 */
export const DossierEventOutput = z.object({
  type: DossierEventType,
  text: z.string(),
  photoCount: z.int().nullable(),
  /**
   * Bouton de la carte : `complement` ouvre la carte du complément (portail : « Consulter et décider » tant
   * qu'une décision est attendue), `photos` le reportage. `null` : pas de bouton.
   */
  action: z.object({ label: z.string(), target: z.enum(['complement', 'photos', 'entry_report']) }).nullable(),
});
export type DossierEventOutput = z.infer<typeof DossierEventOutput>;

/** Complément auquel un message est lié (événement, ou commentaire d'une demande de modification). */
export const MessageComplementRef = z.object({ id: Id, version: z.int(), title: z.string() });

export const MessageOutput = z.object({
  id: Id,
  interventionId: Id,
  authorType: MessageAuthorType,
  authorName: z.string().nullable(),
  body: z.string(),
  createdAt: Timestamp,
  replyToId: Id.nullable(),
  handledAt: Timestamp.nullable(),
  /** Photos jointes (vague 4), servies par `media.file`. */
  media: z.array(MessageMediaOutput),
  /**
   * Vague 6 : `event` = événement du dossier (`body` vide, `event` rendu par `renderConversation`), sinon
   * message écrit ; `complement` : complément lié. Champs facultatif pendant la vague 6 (lots en cours) ; rendu obligatoire par L0 à l’intégration.
   */
  kind: MessageKind.optional(),
  event: DossierEventOutput.nullable().optional(),
  complement: MessageComplementRef.nullable().optional(),
  /**
   * Correctifs (S27) : message ou photo du client arrivé par un **lien partagé** : nom du membre qui a
   * partagé le lien (« via le lien partagé par … »), sinon `null`. Côté garage seulement : jamais dans
   * `PortalMessageOutput`. Facultatif pour les maquettes du web ; toujours renseigné par l'API.
   */
  sharedByName: z.string().nullable().optional(),
});
export type MessageOutput = z.infer<typeof MessageOutput>;

/**
 * Intégration de la vague 7 (02/10/2026) : un message peut ne porter que des photos, comme dans une
 * messagerie : texte vide accepté seulement si `mediaIds` n'est pas vide (`textOrPhotos`).
 */
const EMPTY_MESSAGE = 'Écrivez un message ou joignez une photo';
const textOrPhotos = (v: { body: string; mediaIds?: string[] | undefined }) => v.body.length > 0 || (v.mediaIds?.length ?? 0) > 0;

export const AnswerInput = z.strictObject({
  body: z.string().trim().max(MESSAGE_BODY_MAX),
  replyToId: Id.nullable().optional(),
  /**
   * Photos du dossier jointes (vague 4) : même intervention, même garage, jamais une photo client.
   * Publiées à l'envoi comme pour un complément (copies sans EXIF ni GPS) ; `media.publish` exigé
   * en plus de `question.answer` dès qu'une photo est jointe.
   */
  mediaIds: z.array(Id).max(MESSAGE_MEDIA_MAX).optional(),
}).refine(textOrPhotos, { message: EMPTY_MESSAGE, path: ['body'] });
export const PortalQuestionInput = z.strictObject({
  body: z.string().trim().max(2000),
  /**
   * Photos envoyées par `portal.uploadMedia` (vague 4) : uniquement celles de ce jeton, sur ce dossier,
   * pas encore jointes à un message.
   */
  mediaIds: z.array(Id).max(PORTAL_MEDIA_LIMITS.perMessage).optional(),
}).refine(textOrPhotos, { message: EMPTY_MESSAGE, path: ['body'] });

/** Message tel que vu par le client (sans état de traitement interne). */
export const PortalMessageOutput = z.object({
  id: Id,
  authorType: MessageAuthorType,
  body: z.string(),
  createdAt: Timestamp,
  replyToId: Id.nullable(),
  /** Photos jointes visibles du client (vague 4), servies par `portal.mediaFile`. */
  media: z.array(PortalMessageMediaOutput),
  /** Vague 6 : événement du dossier (carte mise en avant côté client) ou message écrit ; facultatif pendant la vague 6 (lots en cours) ; rendu obligatoire par L0 à l’intégration. */
  kind: MessageKind.optional(),
  event: DossierEventOutput.nullable().optional(),
  complement: MessageComplementRef.nullable().optional(),
});

export const EmailDeliveryOutput = z.object({
  id: Id,
  template: z.string(),
  recipient: z.string(),
  status: EmailDeliveryStatus,
  error: z.string().nullable(),
  createdAt: Timestamp,
  sentAt: Timestamp.nullable(),
});

/**
 * Vague 7 : aperçu du **dernier e-mail envoyé au client** pour un dossier (`interventions.lastEmailPreview`,
 * L4) : rendu à nouveau à partir du journal des envois (modèle, événement d'origine) et de la marque, **sans
 * jeton** : chaque lien personnel du client est remplacé par `PREVIEW_LINK_PLACEHOLDER`. Sans `amounts.view`,
 * les montants du texte sont remplacés par `PREVIEW_AMOUNT_PLACEHOLDER` (`amountsHidden`). `email` vaut `null`
 * si aucun e-mail n'a été envoyé au client pour ce dossier. Seuls les e-mails au client (pas ceux du garage).
 */
export const PREVIEW_LINK_PLACEHOLDER = '[lien personnel du client]';
export const PREVIEW_AMOUNT_PLACEHOLDER = '[montant masqué]';

export const LastEmailPreviewOutput = z.object({
  email: z
    .object({
      deliveryId: Id,
      template: z.string(),
      /** Libellé du modèle (`EMAIL_TEMPLATE_LABELS`). */
      templateLabel: z.string(),
      status: EmailDeliveryStatus,
      sentAt: Timestamp.nullable(),
      createdAt: Timestamp,
      /** Adresse masquée (`maskEmail` du cœur : `j…@exemple.fr`). */
      recipientMasked: z.string(),
      subject: z.string(),
      /** Version texte, liens personnels masqués. */
      text: z.string(),
      /** Version HTML à afficher dans un cadre isolé (`sandbox`, sans script), liens personnels masqués. */
      html: z.string(),
    })
    .nullable(),
  amountsHidden: z.boolean(),
});
export type LastEmailPreviewOutput = z.infer<typeof LastEmailPreviewOutput>;
